import { useMemo } from 'react';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { getListViewProfilesTemplate } from 'apollo/templates/organization';
import { setOrderListSelectedProfileTemplate } from 'apollo/templates/user/mutation.graphql';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { ListViewProfile, ListViewType, OrderListProfileDefaults, OrderListScope } from 'models/organization';

type UseActiveOrderListProfileOptions = {
    enabled?: boolean;
    systemDefault: ListViewProfile;
    scope: OrderListScope;
};

type UseActiveOrderListProfileResult = {
    profiles: ListViewProfile[];
    activeProfile: ListViewProfile;
    activeProfileId: string;
    labDefaultProfileId: string;
    selectProfile: (profileId: string) => void;
    loading: boolean;
};

export const useActiveOrderListProfile = ({
    enabled = true,
    systemDefault,
    scope,
}: UseActiveOrderListProfileOptions): UseActiveOrderListProfileResult => {
    const currentUser = useReactiveVar(currentUserVar);

    const { data, loading } = useQuery<{
        listViewProfiles: ListViewProfile[];
        orderListProfileDefaults: OrderListProfileDefaults | null;
    }>(getListViewProfilesTemplate, {
        fetchPolicy: 'cache-and-network',
        skip: !enabled,
    });

    const [persistSelectedProfile] = useMutation(setOrderListSelectedProfileTemplate);

    const labOrderProfiles = useMemo(
        () => (data?.listViewProfiles ?? []).filter((profile) => profile.listType === ListViewType.LAB_ORDER_LIST),
        [data?.listViewProfiles],
    );

    const labDefaultProfileId = useMemo(() => {
        const defaults = data?.orderListProfileDefaults;
        const fromDefaults = scope === 'MAIN' ? defaults?.mainProfileId : defaults?.workflowProfileId;
        return fromDefaults ?? systemDefault._id;
    }, [data?.orderListProfileDefaults, scope, systemDefault._id]);

    const userSettings = currentUser?.orderListSettings;
    const hasSelected =
        scope === 'MAIN' ? !!userSettings?.hasSelectedMainProfile : !!userSettings?.hasSelectedWorkflowProfile;
    const userSelectedProfileId = hasSelected
        ? scope === 'MAIN'
            ? userSettings?.selectedMainProfileId ?? null
            : userSettings?.selectedWorkflowProfileId ?? null
        : undefined;

    const profilesById = useMemo(() => {
        const map = new Map<string, ListViewProfile>();
        map.set(systemDefault._id, systemDefault);
        for (const profile of labOrderProfiles) {
            map.set(profile._id, profile);
        }
        return map;
    }, [labOrderProfiles, systemDefault]);

    const isInitialLoading = loading && !data;

    const activeProfileId = !hasSelected
        ? profilesById.has(labDefaultProfileId)
            ? labDefaultProfileId
            : systemDefault._id
        : userSelectedProfileId === null
          ? systemDefault._id
          : profilesById.has(userSelectedProfileId!) || isInitialLoading
            ? userSelectedProfileId!
            : profilesById.has(labDefaultProfileId)
              ? labDefaultProfileId
              : systemDefault._id;

    const placeholderProfile: ListViewProfile | null = useMemo(() => {
        if (!isInitialLoading) return null;
        if (!userSelectedProfileId) return null;
        if (profilesById.has(userSelectedProfileId)) return null;
        return {
            _id: userSelectedProfileId,
            name: '',
            listType: ListViewType.LAB_ORDER_LIST,
            columns: [],
        };
    }, [isInitialLoading, userSelectedProfileId, profilesById]);

    const activeProfile = profilesById.get(activeProfileId) ?? placeholderProfile ?? systemDefault;

    const selectProfile = async (profileId: string) => {
        const isSystemDefault = profileId === systemDefault._id;
        const persistedId = isSystemDefault ? null : profileId;

        const previousSettings = currentUser?.orderListSettings ?? {};
        const updatedSettings = {
            ...previousSettings,
            selectedMainProfileId: scope === 'MAIN' ? persistedId : previousSettings.selectedMainProfileId,
            selectedWorkflowProfileId: scope === 'WORKFLOW' ? persistedId : previousSettings.selectedWorkflowProfileId,
            hasSelectedMainProfile: scope === 'MAIN' ? true : previousSettings.hasSelectedMainProfile,
            hasSelectedWorkflowProfile: scope === 'WORKFLOW' ? true : previousSettings.hasSelectedWorkflowProfile,
        };
        currentUserVar({ ...currentUser, orderListSettings: updatedSettings });

        await persistSelectedProfile({
            variables: { scope, profileId: persistedId },
        });
    };

    return {
        profiles: placeholderProfile
            ? [systemDefault, ...labOrderProfiles, placeholderProfile]
            : [systemDefault, ...labOrderProfiles],
        activeProfile,
        activeProfileId,
        labDefaultProfileId,
        selectProfile,
        loading,
    };
};
